import { useState, useEffect } from 'react';
import { Stack, Group, Loader, Center, Button, Text } from '@mantine/core';
import { useSchemaForm } from '@ayocore/platform/client/hooks/useSchemaForm';
import { useAccountingDocumentTransactionRepository } from '@ayocore/erp/accounting/client/rpc';
import accountingDocumentTransactionSchemaDefinition from '@ayocore/erp/accounting/schemas/document-transaction.schema';
import logger from '@ayocore/platform/common/helpers/logger';

interface EditDealExpenseModalProps extends Record<string, unknown> {
  transactionId: number;
  /** Заголовок передаётся через useModal и используется автоматически */
  title?: string;
  /** Вызывается после успешного сохранения */
  onUpdated?: () => void;
}

/**
 * Содержимое модалки редактирования расхода по сделке.
 *
 * Позволяет изменить дату операции, сумму и описание транзакции.
 * Документ-источник не меняется.
 */
export function EditDealExpenseModal({ transactionId, onUpdated }: EditDealExpenseModalProps) {
  const transactionRepository = useAccountingDocumentTransactionRepository();
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const SchemaForm = useSchemaForm(accountingDocumentTransactionSchemaDefinition.updateSchema, {
    hideDescriptions: true,
    customValidation: values => {
      const errors: Record<string, string> = {};
      if (!values.amount || Number(values.amount) <= 0) errors.amount = 'Укажите сумму';
      if (!values.operation_date) errors.operation_date = 'Укажите дату операции';
      return errors;
    },
  });

  useEffect(() => {
    const load = async () => {
      try {
        const tx = await transactionRepository.findById(transactionId);
        if (tx) SchemaForm.setInitialData(tx);
      } catch (err) {
        logger.error('Ошибка при загрузке расхода:', err);
        SchemaForm.setError('Ошибка при загрузке данных');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [transactionId]);

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await transactionRepository.deleteById(transactionId);
      onUpdated?.();
    } catch (err) {
      logger.error('Ошибка при удалении расхода:', err);
      SchemaForm.setError(err instanceof Error ? err.message : 'Ошибка при удалении');
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmit = async (values: typeof SchemaForm.values) => {
    try {
      await transactionRepository.update(transactionId, {
        amount: values.amount as number,
        operation_date: values.operation_date as string,
        description: (values.description as string | null) ?? null,
      });
      SchemaForm.setSuccess('Изменения сохранены');
      setTimeout(() => onUpdated?.(), 800);
      return { success: true };
    } catch (err) {
      logger.error('Ошибка при сохранении расхода:', err);
      SchemaForm.setError(err instanceof Error ? err.message : 'Ошибка при сохранении');
      return { success: false };
    }
  };

  if (loading) {
    return (
      <Center py='xl'>
        <Loader size='sm' />
      </Center>
    );
  }

  return (
    <form onSubmit={SchemaForm.handleSubmit(handleSubmit)}>
      <Stack gap='sm'>
        <SchemaForm.Messages />
        <SchemaForm.Field.operation_date size='xs' />
        <SchemaForm.Field.amount size='xs' />
        <SchemaForm.Field.description size='xs' />
        <Group justify='space-between'>
          {confirmDelete ? (
            <Group gap='xs'>
              <Text size='xs' c='dimmed'>
                Удалить расход?
              </Text>
              <Button size='xs' color='red' loading={deleting} onClick={() => void handleDelete()}>
                Да, удалить
              </Button>
              <Button
                size='xs'
                variant='subtle'
                color='gray'
                onClick={() => setConfirmDelete(false)}
              >
                Отмена
              </Button>
            </Group>
          ) : (
            <Button size='xs' variant='subtle' color='red' onClick={() => setConfirmDelete(true)}>
              Удалить
            </Button>
          )}
          <SchemaForm.buttons.submit label='Сохранить' size='xs' />
        </Group>
      </Stack>
    </form>
  );
}
